import type { ApiErrorBody, RehostChangeDTO } from '@devrobot/shared';

/**
 * Typed API error. Every DevRobot error body is `{ error: { code, message, hint?, details? } }`;
 * `details.blockers` may list several problems, and 428 `confirmation_required` responses carry
 * `details.confirmField` / `details.expected`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly hint?: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, hint?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.hint = hint;
    this.details = details;
  }

  private detail(key: string): unknown {
    const d = this.details;
    return d && typeof d === 'object' && !Array.isArray(d) ? (d as Record<string, unknown>)[key] : undefined;
  }

  /** Multiple problems reported at once (`details.blockers`). */
  get blockers(): string[] {
    const b = this.detail('blockers');
    return Array.isArray(b) ? b.map(String) : [];
  }

  /** Present for 428 confirmation_required responses. */
  get confirmation(): { field: string; expected?: string } | null {
    if (this.status !== 428 && this.code !== 'confirmation_required') return null;
    const field = this.detail('confirmField');
    const expected = this.detail('expected');
    return { field: typeof field === 'string' ? field : 'confirm', expected: typeof expected === 'string' ? expected : undefined };
  }

  /** Applications whose generated hostnames would move (428 with confirmField `confirmMove`). */
  get rehostChanges(): RehostChangeDTO[] {
    const c = this.detail('changes');
    return Array.isArray(c) ? (c as RehostChangeDTO[]) : [];
  }

  /** A conflicting or related job (e.g. "a deploy job is in progress on this target"). */
  get jobId(): string | undefined {
    const j = this.detail('jobId');
    return typeof j === 'string' ? j : undefined;
  }
}

/** Builds an ApiError from any failed response (JSON error body, proxy error page, empty body). */
export async function parseApiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  const e = (body as Partial<ApiErrorBody> | null)?.error;
  if (e && typeof e === 'object' && typeof e.message === 'string') {
    return new ApiError(res.status, typeof e.code === 'string' ? e.code : 'error', e.message, typeof e.hint === 'string' ? e.hint : undefined, e.details);
  }
  if (res.status >= 502 && res.status <= 504) {
    return new ApiError(res.status, 'server_unreachable', `The DevRobot server did not respond (HTTP ${res.status}).`, 'Check that the DevRobot server is running, then retry.');
  }
  const code = res.status === 401 ? 'unauthenticated' : res.status === 403 ? 'forbidden' : res.status === 404 ? 'not_found' : res.status >= 500 ? 'internal_error' : 'http_error';
  return new ApiError(res.status, code, `Request failed with HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''}.`);
}

type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Do not redirect to the sign-in page on 401 (the caller handles it). */
  allowUnauthenticated?: boolean;
  /**
   * Let the request outlive the page that sent it. For the small fire-and-forget POSTs made on
   * the way out of the app -- a click that navigates this very window elsewhere -- which the
   * browser would otherwise cancel at unload. Bodies are capped at 64 KB.
   */
  keepalive?: boolean;
}

let redirecting = false;
let unauthorizedHandler = (): void => {
  if (redirecting || typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (pathname.startsWith('/login')) return;
  redirecting = true;
  window.location.assign(`/login?redirect=${encodeURIComponent(pathname + search)}`);
};

/** Replaces the 401 behaviour (tests). */
export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    params.set(k, v === true ? '1' : String(v));
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  // Cookie-authenticated mutations must carry the CSRF header.
  if (method !== 'GET' && method !== 'HEAD') headers['X-DevRobot-CSRF'] = '1';
  const init: RequestInit = { method, credentials: 'same-origin', headers, signal: opts.signal, keepalive: opts.keepalive };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), init);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network_error', 'Could not reach the DevRobot server.', 'Check your network connection. The request may not have been sent; verify before retrying.');
  }
  if (!res.ok) {
    const error = await parseApiError(res);
    if (res.status === 401 && !opts.allowUnauthenticated) unauthorizedHandler();
    throw error;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
};

/** Encodes one path segment. */
export const seg = (s: string) => encodeURIComponent(s);

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
